import type { CSSObject } from '@emotion/styled';
import { useHasAccess } from '@ice-web-app/shared-hooks';
import { PermissionKeyEnum } from '@ice-web-app/shared-types';
import { Stack } from '@mui/material';
import classnames from 'classnames';
import { forwardRef, useEffect, useState } from 'react';
import type { AnchorHTMLAttributes, ForwardRefRenderFunction, ReactElement, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';
import { DsTypography } from '../../../../components/typography/DsTypography';
import useVerticalMenu from '../../hooks/useVerticalMenu';
import useVerticalNav from '../../hooks/useVerticalNav';
import StyledMenuLabel from '../../styles/StyledMenuLabel';
import StyledMenuPrefix from '../../styles/StyledMenuPrefix';
import StyledMenuSuffix from '../../styles/StyledMenuSuffix';
import StyledVerticalMenuItem from '../../styles/vertical/StyledVerticalMenuItem';
import type { ChildrenType, MenuItemElement, MenuItemExactMatchUrlProps, RootStylesType } from '../../types';
import { menuClasses } from '../../utils/menuClasses';
import { renderMenuIcon } from '../../utils/menuUtils';
import MenuButton from './MenuButton';

export type MenuItemProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'prefix'> &
  RootStylesType &
  Partial<ChildrenType> &
  MenuItemExactMatchUrlProps & {
    icon?: ReactElement;
    badge?: number;
    prefix?: ReactNode;
    suffix?: ReactNode;
    permissions?: PermissionKeyEnum;
    suffixOnClick?: () => void;
    disabled?: boolean;
    target?: string;
    rel?: string;
    component?: string | ReactElement;
    onActiveChange?: (active: boolean) => void;

    /**
     * @ignore
     */
    level?: number;
  };

const MenuItem: ForwardRefRenderFunction<HTMLLIElement, MenuItemProps> = (props, ref) => {
  // Props
  const {
    children,
    icon,
    className,
    prefix,
    suffix,
    permissions,
    suffixOnClick,
    level = 0,
    disabled = false,
    exactMatch = true,
    activeUrl,
    component,
    onActiveChange,
    rootStyles,
    badge,
    ...rest
  } = props;

  // States
  const [active, setActive] = useState(false);

  // Hooks
  const { pathname } = useLocation();
  const { menuItemStyles, renderExpandedMenuItemIcon, textTruncate } = useVerticalMenu();

  const { isCollapsed, isHovered, isPopoutWhenCollapsed, toggleVerticalNav, isToggled, isBreakpointReached } =
    useVerticalNav();

  // Get the styles for the specified element.
  const getMenuItemStyles = (element: MenuItemElement): CSSObject | undefined => {
    // If the menuItemStyles prop is provided, get the styles for the specified element.
    if (menuItemStyles) {
      // Define the parameters that are passed to the style functions.
      const params = { level, disabled, active, isSubmenu: false };

      // Get the style function for the specified element.
      const styleFunction = menuItemStyles[element];

      if (styleFunction) {
        // If the style function is a function, call it and return the result.
        // Otherwise, return the style function itself.
        return typeof styleFunction === 'function' ? styleFunction(params) : styleFunction;
      }
    }
  };

  // Handle the click event.
  const handleClick = () => {
    if (isToggled) {
      toggleVerticalNav();
    }
  };

  // Change active state when the url changes
  useEffect(() => {
    const href = rest.href || (component && typeof component !== 'string' && (component.props as any)?.href);

    if (href) {
      // Check if the current url matches any of the children urls
      if (exactMatch ? pathname === href : activeUrl && pathname.includes(activeUrl)) {
        setActive(true);
      } else {
        setActive(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Call the onActiveChange callback when the active state changes.
  useUpdateEffect(() => {
    onActiveChange?.(active);
  }, [active]);

  // if the user does not have access to view the page
  // const hasAccess = useHasAccess(permissions, 'show');
  // if (!hasAccess) return null;

  return (
    <StyledVerticalMenuItem
      ref={ref}
      className={classnames(
        menuClasses.menuItemRoot,
        { [menuClasses.disabled]: disabled },
        { [menuClasses.active]: active },
        className,
      )}
      level={level}
      isCollapsed={isCollapsed}
      isPopoutWhenCollapsed={isPopoutWhenCollapsed}
      disabled={disabled}
      buttonStyles={getMenuItemStyles('button')}
      menuItemStyles={getMenuItemStyles('root')}
      rootStyles={rootStyles}
    >
      <MenuButton
        className={classnames(menuClasses.button, { [menuClasses.active]: active })}
        component={component}
        tabIndex={disabled ? -1 : 0}
        {...rest}
        onClick={(e) => {
          handleClick();
          rest.onClick?.(e);
        }}
      >
        {/* Menu Item Icon */}
        {renderMenuIcon({
          icon,
          level,
          active,
          disabled,
          renderExpandedMenuItemIcon,
          styles: getMenuItemStyles('icon'),
          isBreakpointReached,
        })}

        {/* Menu Item Prefix */}
        {prefix && (
          <StyledMenuPrefix
            isHovered={isHovered}
            isCollapsed={isCollapsed}
            firstLevel={level === 0}
            className={menuClasses.prefix}
            rootStyles={getMenuItemStyles('prefix')}
          >
            {prefix}
          </StyledMenuPrefix>
        )}

        {/* Menu Item Label */}
        <StyledMenuLabel
          className={menuClasses.label}
          rootStyles={getMenuItemStyles('label')}
          textTruncate={textTruncate}
        >
          {children}
          {badge && (
            <Stack
              justifyContent="center"
              sx={{
                display: 'inline-flex',
                width: 24,
                height: 24,
                borderRadius: '50%',
                mx: 2,
                transform: 'scale(0.8)',
                backgroundColor: (theme) => theme.palette.error.main,
              }}
            >
              <DsTypography color="background.default" variant="caption" align="center">
                {badge}
              </DsTypography>
            </Stack>
          )}
        </StyledMenuLabel>

        {/* Menu Item Suffix */}
        {suffix && (
          <StyledMenuSuffix
            isHovered={isHovered}
            isCollapsed={isCollapsed}
            firstLevel={level === 0}
            className={menuClasses.suffix}
            rootStyles={getMenuItemStyles('suffix')}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              suffixOnClick?.();
            }}
          >
            {suffix}
          </StyledMenuSuffix>
        )}
      </MenuButton>
    </StyledVerticalMenuItem>
  );
};

export default forwardRef(MenuItem);
